import { useEffect, useState, useCallback } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  MagnifyingGlass, ArrowClockwise, WarningCircle, Receipt as ReceiptIcon,
  CheckCircle, CalendarBlank, FloppyDisk, Prohibit, ArrowCounterClockwise, Clock, X, Funnel, CopySimple,
  Plus, Minus, ArrowsOut, Trash,
} from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { money } from "@/lib/format";
import { Card, Loading } from "@/components/riq";
import SortableTable from "@/components/SortableTable";
import { SourceBadge } from "@/components/SourceBadge";
import { useAdminNav } from "@/pages/adminNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogPortal } from "@/components/ui/dialog";
import { toast } from "sonner";

const token = () => localStorage.getItem("riq_token");
const imgUrl = (p) => `${API}/admin/files/${p}?auth=${token()}`;

function ReviewChip({ status, code }) {
  if (code === "duplicate") return <span data-testid="review-chip-duplicate" className="rounded-full bg-[#FFF0D6] text-[#8a5a00] px-1.5 py-0.5 text-[9px] font-bold uppercase">Duplicate</span>;
  if (status === "rejected") return <span data-testid="review-chip-rejected" className="rounded-full bg-[#FFE3E1] text-[#b3261e] px-1.5 py-0.5 text-[9px] font-bold uppercase">Rejected</span>;
  if (status === "needs_date_fix") return <span data-testid="review-chip-needs-fix" className="rounded-full bg-riq-gold/25 text-[#8a5a00] px-1.5 py-0.5 text-[9px] font-bold uppercase">Fix date</span>;
  if (status === "needs_review") return <span data-testid="review-chip-needs-review" className="rounded-full bg-riq-sage text-riq-primary px-1.5 py-0.5 text-[9px] font-bold uppercase">Check total</span>;
  return null;
}

function confTier(pct) {
  if (pct == null) return { c: "#9aa0b4", b: "#eef0f4", l: "—" };
  if (pct >= 95) return { c: "#1f7a48", b: "#E4F7ED" };
  if (pct >= 85) return { c: "#8a5a00", b: "#FFF1D6" };
  return { c: "#b3261e", b: "#FFE3E1" };
}
function Conf({ pct, label }) {
  const t = confTier(pct);
  return <span style={{ color: t.c, background: t.b }} className="rounded-full px-2 py-0.5 text-[10px] font-bold">{label ? `${label} ` : ""}{pct == null ? "—" : `${Math.round(pct)}%`}</span>;
}

function filterLabel(f) {
  if (!f) return null;
  if (f.user_email || f.user_id) return `User: ${f.user_email || f.user_id}`;
  if (f.merchant) return `Merchant: ${f.merchant}`;
  if (f.brand) return `Brand: ${f.brand}`;
  if (f.product) return `Product: ${f.product}`;
  if (f.location) return `Location: ${f.location}`;
  return null;
}

export default function AdminSlips({ incomingFilter }) {
  const { openReceipts } = useAdminNav();
  const [search, setSearch] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [drill, setDrill] = useState(incomingFilter || null);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [rescanning, setRescanning] = useState(false);
  const [lowConf, setLowConf] = useState(85);

  const load = useCallback((q = "", nr = false, fl = false, f = null) => {
    setData(null);
    setSel(new Set());
    const p = new URLSearchParams();
    if (q) p.set("search", q);
    if (nr) p.set("needs_review", "true");
    if (fl) p.set("flagged", "true");
    if (f?.user_id) p.set("user_id", f.user_id);
    if (f?.merchant) p.set("merchant", f.merchant);
    if (f?.product) p.set("product", f.product);
    if (f?.location) p.set("location", f.location);
    if (f?.brand) p.set("brand", f.brand);
    p.set("limit", "200");
    api.get(`/admin/receipts?${p}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  useEffect(() => { setDrill(incomingFilter || null); }, [incomingFilter]);
  useEffect(() => { load(search, needsReview, flagged, drill); /* eslint-disable-next-line */ }, [needsReview, flagged, drill]);

  const clearDrill = () => { setDrill(null); load(search, needsReview, flagged, null); };

  const purgeRejected = async () => {
    if (!window.confirm("Permanently delete ALL existing rejected slips that are NOT duplicates? This removes their images, items and warranties and reverses any Moolas. Duplicates are kept. This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/receipts/purge-rejected");
      toast.success(data.purged ? `Deleted ${data.purged} rejected slip${data.purged === 1 ? "" : "s"}` : "No non-duplicate rejected slips to delete");
      load(search, needsReview, flagged, drill);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not purge rejected slips."); }
  };

  const allIds = (data?.receipts || []).map((r) => r.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => sel.has(id));
  const toggleAll = () => setSel(() => (allSelected ? new Set() : new Set(allIds)));
  const toggleOne = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkDelete = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected slip${ids.length === 1 ? "" : "s"}? Their images, line items and any warranties are removed and any Moolas reversed. This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/receipts/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} slip${data.deleted === 1 ? "" : "s"}`);
      setSel(new Set());
      load(search, needsReview, flagged, drill);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete selected slips."); }
  };

  const pollRescan = (jobId, tid, skipped = 0) => {
    const poll = async () => {
      try {
        const { data: job } = await api.get(`/admin/receipts/bulk-rerun/${jobId}`);
        if (job.status === "done") {
          toast.success(`Re-scanned ${job.total} slip${job.total === 1 ? "" : "s"} · ${job.fixed} updated${job.failed ? ` · ${job.failed} failed` : ""}${skipped ? ` · ${skipped} skipped (no image)` : ""}`, { id: tid });
          setSel(new Set());
          setRescanning(false);
          load(search, needsReview, flagged, drill);
        } else {
          toast.loading(`Re-scanning… ${job.done}/${job.total}`, { id: tid });
          setTimeout(poll, 2000);
        }
      } catch { toast.error("Lost track of the re-scan job — refresh to see results.", { id: tid }); setRescanning(false); }
    };
    setTimeout(poll, 1500);
  };

  const bulkRescan = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    if (!window.confirm(`Re-run AI OCR on ${ids.length} selected slip${ids.length === 1 ? "" : "s"}? This re-reads items, total and date from each stored image and may change their values.`)) return;
    setRescanning(true);
    const tid = toast.loading("Starting re-scan…");
    try {
      const { data } = await api.post("/admin/receipts/bulk-rescan", { ids });
      pollRescan(data.job_id, tid, data.skipped);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start re-scan.", { id: tid });
      setRescanning(false);
    }
  };

  const rescanLowConf = async () => {
    if (!window.confirm(`Find and re-run AI OCR on every slip with any confidence below ${lowConf}%? This runs in the background and may change their values.`)) return;
    setRescanning(true);
    const tid = toast.loading("Finding low-confidence slips…");
    try {
      const { data } = await api.post("/admin/receipts/bulk-rescan-low-conf", { threshold: lowConf });
      if (!data.job_id || !data.total) {
        toast.success(`No slips under ${lowConf}% with a stored image.`, { id: tid });
        setRescanning(false);
        return;
      }
      pollRescan(data.job_id, tid, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start re-scan.", { id: tid });
      setRescanning(false);
    }
  };

  const bulkReview = async (action) => {    const ids = [...sel];
    if (!ids.length) return;
    let reason;
    if (action === "reject") {
      if (!window.confirm(`Reject ${ids.length} selected slip${ids.length === 1 ? "" : "s"}? Non-duplicate rejections reverse any Moolas, claw back referral rewards and delete the record. This cannot be undone.`)) return;
      reason = window.prompt("Reason for rejecting these receipts (optional):", "") ?? undefined;
      if (reason === undefined) return;
    }
    try {
      const { data } = await api.post("/admin/receipts/bulk-review", { ids, action, reason });
      const verb = action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Reset";
      toast.success(`${verb} ${data.processed} slip${data.processed === 1 ? "" : "s"}`);
      setSel(new Set());
      load(search, needsReview, flagged, drill);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update selected slips."); }
  };

  const columns = [
    {
      key: "_select", sortable: false, width: "36px", align: "center",
      label: (
        <input type="checkbox" data-testid="slips-select-all" checked={allSelected}
          onChange={toggleAll} onClick={(e) => e.stopPropagation()}
          className="accent-riq-primary cursor-pointer align-middle" title="Select all" />
      ),
      render: (r) => (
        <input type="checkbox" data-testid={`slip-select-${r.id}`} checked={sel.has(r.id)}
          onChange={() => toggleOne(r.id)} onClick={(e) => e.stopPropagation()}
          className="accent-riq-primary cursor-pointer align-middle" />
      ),
    },
    {
      key: "merchant_name", label: "Merchant", type: "text",
      render: (r) => (
        <button
          data-testid={`slip-merchant-link-${r.id}`}
          onClick={(e) => { e.stopPropagation(); openReceipts({ merchant: r.merchant_name }); }}
          className="font-semibold text-riq-primary hover:underline text-left flex items-center gap-1.5 max-w-[220px]"
          title="Show all receipts from this merchant"
        >
          <span className="truncate">{r.merchant_name || "Unknown"}</span>
          <ReviewChip status={r.review_status} code={r.review_code} />
          <SourceBadge source={r.source} iconOnly />
        </button>
      ),
    },
    {
      key: "user_email", label: "User", type: "text",
      render: (r) => (
        <button
          data-testid={`slip-user-link-${r.id}`}
          onClick={(e) => { e.stopPropagation(); openReceipts({ user_id: r.user_id, user_email: r.user_email }); }}
          className="text-neutral-600 hover:text-riq-primary hover:underline truncate max-w-[200px] block"
          title="Show all receipts from this user"
        >
          {r.user_email}
        </button>
      ),
    },
    { key: "created_at", label: "Added", type: "date", render: (r) => <span className="text-neutral-600 whitespace-nowrap">{(r.created_at || "").slice(0, 10) || "—"}</span> },
    { key: "date", label: "Receipt date", type: "date", render: (r) => <span className="text-muted-foreground whitespace-nowrap">{r.date || "—"}</span> },
    { key: "min_conf", label: "Conf", type: "number", align: "right", render: (r) => <Conf pct={r.min_conf} /> },
    { key: "total", label: "Total", type: "number", align: "right", render: (r) => <span className="font-semibold whitespace-nowrap">{money(r.total, r.currency)}</span> },
  ];

  if (data === false) return <div className="text-muted-foreground">Could not load slips.</div>;

  return (
    <div className="space-y-5" data-testid="admin-slips">
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={(e) => { e.preventDefault(); load(search.trim(), needsReview, flagged, drill); }} className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="slips-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by merchant or user email…" className="riq-input !pl-10 w-full" />
        </form>
        <button data-testid="slips-flagged" onClick={() => setFlagged((v) => !v)}
          className={`flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${flagged ? "bg-[#b3261e] text-white" : "border border-riq-border text-neutral-600 hover:border-[#b3261e]"}`}>
          <Prohibit size={16} weight="bold" /> Flagged
        </button>
        <button data-testid="slips-needs-review" onClick={() => setNeedsReview((v) => !v)}
          className={`flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${needsReview ? "bg-riq-primary text-white" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}>
          <WarningCircle size={16} weight="bold" /> Needs review
        </button>
        <button data-testid="slips-find-duplicates" onClick={() => setDupOpen(true)}
          className="flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium border border-riq-border text-neutral-600 hover:border-riq-primary transition-colors">
          <CopySimple size={16} weight="bold" /> Find duplicates
        </button>
        <button data-testid="slips-purge-rejected" onClick={purgeRejected}
          title="Deletes older non-duplicate rejected slips. New ones auto-delete on rejection, so this is mainly for legacy data."
          className="flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium border border-[#b3261e] text-[#b3261e] hover:bg-[#FFE3E1] transition-colors">
          <Trash size={16} weight="bold" /> Purge rejected
        </button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Bulk re-scan slips below</span>
        <select data-testid="lowconf-threshold" value={lowConf} onChange={(e) => setLowConf(Number(e.target.value))}
          className="rounded-full border border-riq-border text-sm px-3 py-1.5 text-neutral-700 focus:outline-none focus:ring-2 focus:ring-riq-primary/30">
          <option value={75}>75% confidence</option>
          <option value={85}>85% confidence</option>
          <option value={95}>95% confidence</option>
        </select>
        <button data-testid="slips-rescan-lowconf" onClick={rescanLowConf} disabled={rescanning}
          className="flex items-center gap-1.5 rounded-full border border-riq-primary text-riq-primary px-4 py-1.5 text-sm font-semibold hover:bg-riq-secondary disabled:opacity-50 transition-colors">
          <ArrowClockwise size={15} weight="bold" className={rescanning ? "animate-spin" : ""} /> Re-scan low-confidence
        </button>
      </div>

      {drill && (
        <div data-testid="slips-filter-chip" className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-riq-primary/10 text-riq-primary px-3 py-1.5 text-sm font-medium">
            <Funnel size={14} weight="fill" /> {filterLabel(drill)}
            <button data-testid="slips-filter-clear" onClick={clearDrill} className="hover:opacity-70"><X size={14} weight="bold" /></button>
          </span>
        </div>
      )}

      {sel.size > 0 && (
        <div data-testid="slips-bulk-bar" className="flex items-center justify-between rounded-xl bg-riq-primary/10 border border-riq-primary/20 px-4 py-2.5">
          <span className="text-sm font-medium text-riq-primary">{sel.size} slip{sel.size === 1 ? "" : "s"} selected</span>
          <div className="flex items-center gap-2">
            <button data-testid="slips-bulk-clear" onClick={() => setSel(new Set())}
              className="text-sm text-neutral-600 hover:text-riq-primary px-3 py-1.5">Clear</button>
            <button data-testid="slips-bulk-rescan" onClick={bulkRescan} disabled={rescanning}
              className="flex items-center gap-1.5 rounded-full border border-riq-primary text-riq-primary px-4 py-2 text-sm font-semibold hover:bg-riq-secondary disabled:opacity-50 transition-colors">
              <ArrowClockwise size={15} weight="bold" className={rescanning ? "animate-spin" : ""} /> Re-run AI
            </button>
            <button data-testid="slips-bulk-approve" onClick={() => bulkReview("approve")}
              className="flex items-center gap-1.5 rounded-full bg-[#1f7a48] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition-opacity">
              <CheckCircle size={15} weight="bold" /> Approve
            </button>
            <button data-testid="slips-bulk-reject" onClick={() => bulkReview("reject")}
              className="flex items-center gap-1.5 rounded-full border border-[#b3261e] text-[#b3261e] px-4 py-2 text-sm font-semibold hover:bg-[#FFE3E1] transition-colors">
              <Prohibit size={15} weight="bold" /> Reject
            </button>
            <button data-testid="slips-bulk-delete" onClick={bulkDelete}
              className="flex items-center gap-1.5 rounded-full bg-[#b3261e] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition-opacity">
              <Trash size={15} weight="bold" /> Delete
            </button>
          </div>
        </div>
      )}

      {!data ? <Loading /> : (
        <Card className="!p-0 overflow-hidden">
          <div className="px-4 py-3 bg-secondary/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {data.total} slip{data.total === 1 ? "" : "s"}{flagged ? " flagged" : needsReview ? " needing review" : ""}
          </div>
          <SortableTable
            columns={columns}
            rows={data.receipts}
            defaultSort={[{ key: "created_at", dir: "desc" }, { key: "date", dir: "desc" }]}
            rowKey="id"
            onRowClick={(r) => setOpenId(r.id)}
            testIdPrefix="slip-row"
            empty="No slips found."
          />
        </Card>
      )}

      <SlipDialog id={openId} onClose={() => setOpenId(null)} onChanged={() => load(search, needsReview, flagged, drill)} />
      <DuplicatesDialog open={dupOpen} onClose={() => setDupOpen(false)} onDone={() => load(search, needsReview, flagged, drill)} />
    </div>
  );
}

function DuplicatesDialog({ open, onClose, onDone }) {
  const [mode, setMode] = useState("user"); // 'user' | 'cross'
  const [merchant, setMerchant] = useState("");
  const [debMerchant, setDebMerchant] = useState("");
  const [scan, setScan] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => { const t = setTimeout(() => setDebMerchant(merchant.trim()), 400); return () => clearTimeout(t); }, [merchant]);

  useEffect(() => {
    if (!open) { setScan(null); setSel(new Set()); setMode("user"); setMerchant(""); return; }
    setScan(null);
    const url = mode === "cross"
      ? `/admin/duplicates/cross-user${debMerchant ? `?merchant=${encodeURIComponent(debMerchant)}` : ""}`
      : "/admin/duplicates/scan";
    api.get(url).then((r) => {
      setScan(r.data);
      const s = new Set();
      r.data.groups.forEach((g) => g.dupes.forEach((d) => s.add(d.id)));
      setSel(s);
    }).catch(() => setScan(false));
  }, [open, mode, debMerchant]);

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const cross = mode === "cross";

  const reject = async () => {
    if (sel.size === 0) { toast.error("Nothing selected."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/duplicates/reject", { receipt_ids: [...sel] });
      const n = data.cleaned ?? data.rejected ?? 0;
      toast.success(`Cleared Moola on ${n} duplicate${n === 1 ? "" : "s"} · entries kept${data.moolas_reversed ? ` · ${data.moolas_reversed} Moolas reversed` : ""}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not process duplicates."); }
    finally { setBusy(false); }
  };

  const cleanupAll = async () => {
    if (!window.confirm(`Process ALL cross-user duplicates${debMerchant ? ` for "${debMerchant}"` : " across every merchant"}? The earliest submitter is kept; every copy stays visible, but each duplicate's Moolas are reversed and any linked referral reward clawed back.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/duplicates/cross-user/cleanup", { merchant: debMerchant || null });
      const n = data.cleaned ?? data.rejected ?? 0;
      toast.success(n ? `Processed ${n} duplicate${n === 1 ? "" : "s"} · entries kept · ${data.moolas_reversed} Moolas reversed` : "No cross-user duplicates to clean up");
      onDone?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CopySimple size={20} weight="duotone" className="text-riq-primary" /> Duplicate cleanup</DialogTitle>
          <DialogDescription>
            {cross
              ? "Same date · same total · same-or-similar merchant across DIFFERENT users — suspected shared-receipt fraud. The earliest submitter is kept; duplicates stay visible but lose their Moola."
              : "Same user · same date · same total · same-or-similar merchant. The earliest scan is kept; tick the duplicates to clear their Moola (the entries are kept, not deleted)."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-1">
          <div className="inline-flex rounded-full bg-secondary p-1 text-xs font-semibold">
            <button data-testid="dup-mode-user" onClick={() => setMode("user")}
              className={`px-3 py-1.5 rounded-full transition-colors ${!cross ? "bg-white text-riq-primary shadow-sm" : "text-neutral-500"}`}>Per user</button>
            <button data-testid="dup-mode-cross" onClick={() => setMode("cross")}
              className={`px-3 py-1.5 rounded-full transition-colors ${cross ? "bg-white text-riq-primary shadow-sm" : "text-neutral-500"}`}>Across all users</button>
          </div>
          {cross && (
            <input data-testid="dup-merchant-filter" value={merchant} onChange={(e) => setMerchant(e.target.value)}
              placeholder="Filter by merchant (optional)…" className="riq-input !py-2 !text-sm flex-1" />
          )}
        </div>

        {scan === false ? <div className="py-8 text-center text-sm text-muted-foreground">Could not run the scan.</div>
          : !scan ? <div className="py-10"><Loading label="Scanning for duplicates…" /></div>
          : scan.groups.length === 0 ? (
            <div className="py-10 text-center">
              <CheckCircle size={40} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
              <div className="font-head font-semibold text-riq-primary">No duplicates found</div>
              <div className="text-sm text-muted-foreground mt-1">{cross ? "No shared receipts across users." : "Every receipt looks unique. Nice and clean."}</div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">Found <b className="text-riq-primary">{scan.duplicate_count}</b> duplicate{scan.duplicate_count === 1 ? "" : "s"} across <b className="text-riq-primary">{scan.group_count}</b> group{scan.group_count === 1 ? "" : "s"}.</div>
              <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1">
                {scan.groups.map((g, i) => (
                  <div key={i} data-testid={`dup-group-${i}`} className="rounded-xl border border-riq-border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-riq-primary truncate">{g.merchant || "Unknown"} · {money(g.total, "ZAR")}</div>
                      <div className="text-xs text-muted-foreground">{g.date}{cross ? ` · ${g.distinct_users} users` : ` · ${g.user_email}`}</div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-1.5 mb-1.5">
                      <CheckCircle size={14} weight="fill" className="shrink-0" /> Keep (earliest): {cross ? g.keep.user_email : "added"} {(g.keep.created_at || "").slice(0, 10)}
                    </div>
                    <div className="space-y-1">
                      {g.dupes.map((d) => (
                        <label key={d.id} data-testid={`dup-item-${d.id}`} className="flex items-center gap-2 text-sm px-2.5 py-1.5 rounded-lg hover:bg-secondary/50 cursor-pointer">
                          <input type="checkbox" checked={sel.has(d.id)} onChange={() => toggle(d.id)} className="accent-riq-primary" />
                          <span className="flex-1 truncate">{cross ? d.user_email : d.merchant_name}</span>
                          {cross && d.item_match != null && <span className="text-[10px] font-bold text-neutral-500" title="Item overlap with the kept receipt">{d.item_match}% items</span>}
                          <span className="text-xs text-muted-foreground">added {(d.created_at || "").slice(0, 10)}</span>
                          {d.moolas_awarded > 0 && <span className="text-[10px] font-bold text-amber-600">−{d.moolas_awarded} 🪙</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-1 gap-2">
                <span className="text-sm text-muted-foreground">{sel.size} selected · Moola will be reversed</span>
                <div className="flex items-center gap-2">
                  {cross && (
                    <button data-testid="dup-cleanup-all-btn" onClick={cleanupAll} disabled={busy}
                      className="flex items-center gap-1.5 rounded-full border border-[#8a5a00] text-[#8a5a00] px-4 py-2 text-sm font-semibold disabled:opacity-50 hover:bg-[#FFF0D6]">
                      Clean up all
                    </button>
                  )}
                  <button data-testid="dup-reject-btn" onClick={reject} disabled={busy || sel.size === 0}
                    className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-5 py-2 text-sm font-semibold disabled:opacity-50">
                    <Prohibit size={15} weight="bold" /> {busy ? "Processing…" : `Clear Moola · ${sel.size}`}
                  </button>
                </div>
              </div>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

function SlipDialog({ id, onClose, onChanged }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dateVal, setDateVal] = useState("");
  const [zoomSrc, setZoomSrc] = useState(null);

  useEffect(() => {
    if (!id) { setRec(null); return; }
    setLoading(true);
    api.get(`/admin/receipts/${id}`).then((r) => { setRec(r.data); setDateVal(r.data.date || ""); })
      .catch(() => toast.error("Could not load slip.")).finally(() => setLoading(false));
  }, [id]);

  const conf = rec?.confidence || {};
  const paths = rec?.storage_paths || [];

  const saveDate = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) { toast.error("Use format YYYY-MM-DD"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/receipts/${id}/date`, { date: dateVal });
      setRec((r) => ({ ...r, date: data.date, to_be_confirmed: false }));
      toast.success("Date corrected");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save date."); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    const tid = toast.loading("Re-running OCR from the slip…");
    try {
      const { data } = await api.post(`/admin/receipts/${id}/regenerate`);
      setRec(data); setDateVal(data.date || "");
      toast.success("OCR regenerated", { id: tid });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Regeneration failed.", { id: tid }); }
    finally { setBusy(false); }
  };

  const regenTotals = async () => {
    setBusy(true);
    const tid = toast.loading("Re-reading total & date…");
    try {
      const { data } = await api.post(`/receipts/${id}/regenerate-totals`);
      setRec(data); setDateVal(data.date || "");
      toast.success("Total & date re-read from slip", { id: tid });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-run failed.", { id: tid }); }
    finally { setBusy(false); }
  };

  const review = async (action) => {
    let reason;
    if (action === "reject") {
      reason = window.prompt("Reason for rejecting this receipt (optional):", "") ?? undefined;
      if (reason === undefined) return; // cancelled
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/receipts/${id}/review`, { action, reason });
      if (action === "reject" && data?.deleted) {
        toast.success("Receipt rejected & deleted");
        onChanged?.();
        onClose();
        return;
      }
      setRec((r) => ({ ...r, ...data }));
      toast.success(action === "approve" ? "Receipt approved" : action === "reject" ? "Receipt rejected" : "Review reset");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update review."); }
    finally { setBusy(false); }
  };

  const deleteRecord = async () => {
    if (!window.confirm("Permanently delete this receipt? Its image, line items and any warranties are removed and any Moolas it earned are reversed. This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/receipts/${id}`);
      toast.success("Receipt deleted");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete receipt."); }
    finally { setBusy(false); }
  };

  const rs = rec?.review_status || "approved";

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="slip-dialog">
        <DialogHeader>
          <DialogTitle>Slip review</DialogTitle>
          <DialogDescription className="sr-only">Review the scanned slip, AI inputs and confidence</DialogDescription>
        </DialogHeader>

        {loading || !rec ? <div className="py-10"><Loading /></div> : (
          <div className="grid md:grid-cols-2 gap-5">
            {/* Slip image */}
            <div className="space-y-3">
              <div className="text-xs font-semibold text-muted-foreground">Till slip{paths.length > 1 ? "s" : ""}</div>
              {paths.length ? paths.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setZoomSrc(imgUrl(p))}
                  data-testid={`slip-image-btn-${i}`}
                  className="block w-full cursor-zoom-in"
                >
                  <img data-testid={`slip-image-${i}`} src={imgUrl(p)} alt="Slip" className="w-full rounded-xl border border-riq-border object-contain max-h-[420px] bg-neutral-50" />
                </button>
              )) : <div className="rounded-xl border border-dashed border-riq-border p-8 text-center text-sm text-muted-foreground">No image stored</div>}
            </div>

            {/* Inputs */}
            <div className="space-y-4">
              {rs !== "approved" && (
                <div data-testid="review-status-banner" className={`rounded-xl p-3 text-xs font-medium flex items-start gap-2 ${rs === "rejected" ? "bg-[#FFE3E1] text-[#b3261e]" : "bg-riq-gold/20 text-[#8a5a00]"}`}>
                  {rs === "rejected" ? <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" /> : <Clock size={16} weight="fill" className="shrink-0 mt-0.5" />}
                  <span>{rec.review_reason || (rs === "rejected" ? "Rejected." : "Needs date confirmation.")}{rec.review_locked ? " (admin decision)" : ""}</span>
                </div>
              )}
              {rec.to_be_confirmed && rs === "approved" && (
                <div data-testid="tbc-banner" className="rounded-xl bg-[#FFE3E1] text-[#b3261e] p-3 text-xs font-medium flex items-start gap-2">
                  <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
                  <span>Date needs confirming{rec.original_ai_date ? ` — AI read "${rec.original_ai_date}".` : "."}</span>
                </div>
              )}

              <Field label="Merchant" conf={conf.merchant}><div className="text-sm font-semibold text-riq-primary">{rec.merchant_name || "—"}</div></Field>

              <Field label="Date" conf={conf.date}>
                <div className="flex items-center gap-2">
                  <input data-testid="slip-date-input" type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="riq-input !py-2 !text-sm flex-1" />
                  <button data-testid="slip-date-save" onClick={saveDate} disabled={busy} className="rounded-xl bg-riq-primary text-white px-3 py-2 text-sm font-medium disabled:opacity-50 flex items-center gap-1">
                    <FloppyDisk size={15} weight="bold" /> Save
                  </button>
                </div>
              </Field>

              <Field label="Total" conf={conf.total}><div className="text-sm font-semibold text-riq-primary">{money(rec.total, rec.currency)}</div></Field>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-muted-foreground">Items ({rec.items?.length || 0})</span>
                  <Conf pct={conf.items} label="items" />
                </div>
                <div className="rounded-xl border border-riq-border divide-y divide-riq-border max-h-40 overflow-y-auto">
                  {(rec.items || []).map((it, i) => (
                    <div key={i} className="flex items-center px-3 py-2 text-sm gap-2">
                      <span className="flex-1 truncate">{it.canonical_name || it.description}</span>
                      {it.confidence != null && <Conf pct={it.confidence} />}
                      <span className="w-16 text-right text-muted-foreground">{it.quantity}×</span>
                      <span className="w-20 text-right font-medium">{money(it.total_price ?? it.unit_price, rec.currency)}</span>
                    </div>
                  ))}
                  {!(rec.items || []).length && <div className="px-3 py-4 text-center text-xs text-muted-foreground">No items</div>}
                </div>
              </div>

              <button data-testid="slip-regenerate" onClick={regenerate} disabled={busy || !paths.length}
                className="w-full rounded-xl border border-riq-primary text-riq-primary py-2.5 text-sm font-semibold hover:bg-riq-secondary disabled:opacity-50 flex items-center justify-center gap-1.5">
                <ArrowClockwise size={16} weight="bold" className={busy ? "animate-spin" : ""} /> Regenerate AI OCR from slip
              </button>
              <button data-testid="slip-regen-totals" onClick={regenTotals} disabled={busy || !paths.length}
                className="w-full rounded-xl border border-riq-border py-2.5 text-sm font-semibold hover:border-riq-primary text-neutral-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
                <ArrowClockwise size={15} weight="bold" className={busy ? "animate-spin" : ""} /> Re-run AI: total &amp; date only
              </button>

              <div className="grid grid-cols-3 gap-2">
                <button data-testid="slip-approve" onClick={() => review("approve")} disabled={busy}
                  className="rounded-xl bg-[#1f7a48] text-white py-2.5 text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1"><CheckCircle size={15} weight="bold" /> Approve</button>
                <button data-testid="slip-reject" onClick={() => review("reject")} disabled={busy}
                  className="rounded-xl bg-[#b3261e] text-white py-2.5 text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1"><Prohibit size={15} weight="bold" /> Reject</button>
                <button data-testid="slip-reset" onClick={() => review("reset")} disabled={busy}
                  className="rounded-xl border border-riq-border text-neutral-600 py-2.5 text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1"><ArrowCounterClockwise size={15} weight="bold" /> Auto</button>
              </div>
              <div className="text-[11px] text-muted-foreground text-center">Approve / Reject overrides auto-rules. "Auto" re-runs duplicate & date checks. Rejecting a non-duplicate slip deletes it and reverses any Moolas.</div>
              <button data-testid="slip-delete" onClick={deleteRecord} disabled={busy}
                className="w-full rounded-xl border border-[#b3261e] text-[#b3261e] py-2.5 text-xs font-semibold hover:bg-[#FFE3E1] disabled:opacity-50 flex items-center justify-center gap-1.5">
                <Trash size={15} weight="bold" /> Delete permanently
              </button>
              <div className="text-[11px] text-muted-foreground text-center">Scanned by {rec.user_email}</div>
            </div>
          </div>
        )}
      </DialogContent>

      <Dialog open={!!zoomSrc} onOpenChange={(o) => !o && setZoomSrc(null)}>
        <DialogPortal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[9998] bg-black/90 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            className="fixed inset-0 z-[9999] focus:outline-none"
            data-testid="slip-zoom-overlay"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <DialogTitle className="sr-only">Slip image</DialogTitle>
            <DialogDescription className="sr-only">Pinch, scroll or use the buttons to zoom the till slip.</DialogDescription>
            {zoomSrc && (
              <TransformWrapper
                initialScale={1}
                minScale={1}
                maxScale={6}
                doubleClick={{ mode: "toggle", step: 2 }}
                wheel={{ step: 0.2 }}
                pinch={{ step: 5 }}
                centerOnInit
              >
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <>
                    <button
                      type="button"
                      data-testid="slip-zoom-close"
                      onClick={() => setZoomSrc(null)}
                      className="absolute top-4 right-4 z-10 rounded-full bg-white/15 hover:bg-white/25 text-white p-2.5 backdrop-blur-sm"
                      aria-label="Close"
                    >
                      <X size={22} weight="bold" />
                    </button>

                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-2 py-1.5">
                      <button type="button" data-testid="slip-zoom-out" onClick={() => zoomOut()} aria-label="Zoom out"
                        className="rounded-full text-white p-2 hover:bg-white/20"><Minus size={18} weight="bold" /></button>
                      <button type="button" data-testid="slip-zoom-reset" onClick={() => resetTransform()} aria-label="Reset zoom"
                        className="rounded-full text-white p-2 hover:bg-white/20"><ArrowsOut size={18} weight="bold" /></button>
                      <button type="button" data-testid="slip-zoom-in" onClick={() => zoomIn()} aria-label="Zoom in"
                        className="rounded-full text-white p-2 hover:bg-white/20"><Plus size={18} weight="bold" /></button>
                    </div>

                    <TransformComponent
                      wrapperStyle={{ width: "100%", height: "100%" }}
                      contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <img
                        src={zoomSrc}
                        alt="Slip"
                        className="max-w-[100vw] max-h-[100vh] object-contain select-none"
                        draggable={false}
                        data-testid="slip-zoom-image"
                      />
                    </TransformComponent>
                  </>
                )}
              </TransformWrapper>
            )}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </Dialog>
  );
}

function Field({ label, conf, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-muted-foreground">{label}</span>
        <Conf pct={conf} />
      </div>
      {children}
    </div>
  );
}
